import { useQuery } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useEffect, useRef } from "react";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

interface RequestParams {
  token: string;
  symbolList: string[];
  lang: Lang;
}

const responseDataSchema = z.object({
  data: z.record(
    z.object({
      companyName: z.string().nullish(),
      dividend: z.number().nullish(),
      chip: z.number().nullish(),
      swing: z.number().nullish(),
      trend: z.number().nullish(),
      value: z.number().nullish(),
      logo: z.string().nullish(),
    })
  ),
  message: z.string(),
});

type SymbolsScoreData = z.infer<typeof responseDataSchema>["data"];

export const getSymbolsScore = async ({
  token,
  symbolList,
  lang,
}: RequestParams): Promise<SymbolsScoreData> => {
  const endpoint = "/symbol/score";
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "X-User-Profile": token,
      "Content-Type": "application/json",
      "Accept-Language": lang,
    },
    body: JSON.stringify({ symbolList }),
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseDataSchema.parse(await res.json()).data;
};

export const useSymbolsScore = ({ symbolList }: { symbolList: string[] }) => {
  const { backendToken: token } = useAuth();
  const prevSymbols = useRef<Set<string>>(new Set([]));
  const { lang } = useLang();

  const query = useQuery(
    ["get-symbols-score", token, symbolList, lang],
    () => getSymbolsScore({ token: token ?? "", symbolList, lang }),
    {
      enabled:
        !!token &&
        symbolList.length > 0 &&
        !isEqual(prevSymbols.current, new Set(symbolList)),
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (query.isSuccess) prevSymbols.current = new Set(symbolList);
    return () => {
      prevSymbols.current = new Set([]);
    };
  }, [query.isSuccess, symbolList]);

  return query;
};
